import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, ViewChild, Output, ElementRef } from '@angular/core';
import { debounceTime, distinctUntilChanged, filter, fromEvent, map, Observable, Subscription, switchMap } from 'rxjs';

@Component({
    selector: 'app-item-search',
    standalone: false,
    templateUrl: './item-search.component.html',
    styleUrl: './item-search.component.css'
})
export class ItemSearchComponent {
    @ViewChild('itemSearch') searchInput: ElementRef = new ElementRef('');
    @Output() selectItem = new EventEmitter<ItemSearchResult>();

    subscription: Subscription | null = null;
    query: string = '';

    results: ItemSearchResult[] = [];

    active: boolean = false;
    searching: boolean = false;

    selected: ItemSearchResult = new ItemSearchResult();

    // instantiate with a placeholder
    clearResultsBound: EventListenerOrEventListenerObject = () => { };

    constructor(private http: HttpClient) {

    }

    ngAfterViewInit(): void {

        this.clearResultsBound = this.clearResults.bind(this);

        // there won't be a search input element if this component is read only
        if (this.searchInput) {
            this.subscription = fromEvent(this.searchInput.nativeElement, 'input')
                .pipe(
                    map(() => this.query),
                    filter(q => {

                        this.activate();
                        this.searching = true;

                        const search = q.length >= 2;

                        if (!search) {
                            // not enough chars to search, clear any previous results
                            this.results.length = 0;
                        }

                        return search;
                    }),
                    debounceTime(10),
                    distinctUntilChanged(),
                    switchMap(() => {

                        this.results.length = 0;

                        return this.doSearch();
                    })
                ).subscribe(results => {

                    results.forEach(r => this.results.push(r));

                    this.searching = false;

                });
        }

    }

    doSearch(): Observable<ItemSearchResult[]> {
        return this.http.get('api/home/search', { params: { q: this.query } })
            .pipe(
                map(response => {

                    const results: ItemSearchResult[] = [];

                    if (response instanceof Array) {
                        response.forEach((r: any) => results.push(new ItemSearchResult(r)));
                    }

                    return results;
                })
            );
    }

    selectResult(result: ItemSearchResult): void {
        // emit the event of selection
        this.selectItem.emit(result);

        // set the selected result on this component
        this.selected = result;

        // clear results and state
        this.clearResults();

        this.query = result.description

    }

    clearResults(): void {

        // update (or revert) value in the input to the actual
        // description (make + model) of the selected model.
        // it will be empty if there is still no model selected
        this.query = this.selected.description;

        this.results.length = 0;
        this.active = false;
        this.searching = false;

        // remove event listener that was added to catch clicks
        // to remove the results list
        window.document.removeEventListener('click', this.clearResultsBound);

    }

    activate() {

        if (!this.active) {
            this.active = true;
            // add click listener to the body so that we can remove the results
            // list when user clicks away without selecting a model
            window.document.addEventListener('click', this.clearResultsBound);
        }

    }

    ngOnDestroy(): void {
        // remove the subscription for input updates
        this.subscription?.unsubscribe();

        if (this.active) {
            // if in the active state the event listener on the body is still there
            // so be sure to remove it
            window.document.removeEventListener('click', this.clearResultsBound);
        }
    }


}

export class ItemSearchResult {
    sku: string = '';
    description: string = '';

    constructor(item?: any) {

        if (item) {
            this.sku = item.sku;
            this.description = item.description;
        }
    }
}
